import { inject, Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { HttpErrorResponse } from "@angular/common/http";
import {
  catchError,
  EMPTY,
  finalize,
  Observable,
  of,
  switchMap,
  tap,
} from "rxjs";
import { OnboardingFlowService } from "src/app/pages/onboarding/onboarding-flow.service";
import { OnboardingModalService } from "src/app/pages/onboarding/onboarding-modal.service";
import { StoreService } from "src/app/services/apps/store/store.service";
import { ConfirmComponent } from "src/app/shared/components/confirm-generic/confirm.component";
import { LoaderService } from "src/app/shared/components/loader/loader.service";
import { ToastService } from "src/app/shared/services/toast.service";
import { eventAction } from "src/app/shared/services/view-context-history-state.service";
import {
  ProviderClaimTypesGetDTO,
  ProviderGetDTO,
} from "../../providers/provider.model";
import { StoreGetDTO, StoreLinkQueryParams } from "./store";
import { StoreUpdateDeliveryDialogComponent } from "./update-store/store-update-delivery-dialog.component";
import { StoreUpdateDialogComponent } from "./update-store/store-update-dialog.component";
import { StoreUpdateThresholdDialogComponent } from "./update-store/store-update-threshold-dialog.component";

@Injectable({
  providedIn: "root",
})
export class StoreFacadeService {
  private storeService = inject(StoreService);
  private dialog = inject(MatDialog);
  private loaderSvc = inject(LoaderService);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  private onboardingModalService = inject(OnboardingModalService);
  private onboardingFlow = inject(OnboardingFlowService);

  /**
   * Builds a map of provider value -> provider name
   */
  buildProviderMap(providers: ProviderGetDTO[]): Record<string, string> {
    const providerMap: Record<string, string> = {};
    providers.forEach((provider) => {
      providerMap[provider.value] = provider.name;
    });
    return providerMap;
  }

  /**
   * Normalizes the provider value for the logo filename
   */
  normalizeProviderValue(providerValue: string | undefined): string {
    return providerValue
      ? providerValue.toString().trim().toLowerCase().replace(/\s+/g, "-")
      : "";
  }

  /**
   * Gets the provider name
   */
  getProviderName(
    providerValue: string | undefined,
    providerNameByValue: Record<string, string>,
  ): string {
    if (!providerValue) {
      return "";
    }
    return providerNameByValue[providerValue] || providerValue;
  }

  /**
   * Gets the provider logo path
   */
  getProviderLogo(providerValue: string | undefined): string {
    const logoName = this.normalizeProviderValue(providerValue);
    return `assets/images/providers-logos/${logoName}.png`;
  }

  /**
   * Filters stores by search and provider
   */
  applyFilter(
    searchText: string,
    selectedProvider: string,
    stores: StoreGetDTO[],
    providerNameByValue: Record<string, string>,
  ): StoreGetDTO[] {
    const searchStr = (searchText || "").toString().trim().toLowerCase();
    const providerValue = (selectedProvider || "").toString().trim();

    if (!searchStr && !providerValue) {
      return stores;
    }

    return stores.filter((store) => {
      const matchedProviderName = this.getProviderName(
        store.provider,
        providerNameByValue,
      );
      const matchesSearch =
        !searchStr ||
        store.storeId?.toString().toLowerCase().includes(searchStr) ||
        store.name?.toLowerCase().includes(searchStr) ||
        matchedProviderName?.toLowerCase().includes(searchStr);
      const matchesProvider =
        !providerValue || store.provider === providerValue;
      return matchesSearch && matchesProvider;
    });
  }

  /**
   * Handles the flow when the store was not found
   */
  handleStoreNotFound(
    param: StoreLinkQueryParams,
    activatedRoute: ActivatedRoute,
    setViewState: (action: eventAction, description: string) => void,
    clearLinkQueryParams: () => void,
  ): Observable<void> {
    setViewState(
      "modal_opened",
      `User attempted to open the modal for update store with id: ${param.id} after adding a new store but the store could not be found in the current view state`,
    );

    const dialogRef = this.dialog.open(ConfirmComponent, {
      data: {
        message: `${this.translate.instant(
          "messages.The store could not be found.",
        )} ${this.translate.instant("stores.unexpectedError")}${
          param.error_code ? `\nError: ${param.error_code}` : ""
        }`,
        clearButtonClose: true,
      },
    });

    return dialogRef.afterClosed().pipe(
      tap(() => {
        clearLinkQueryParams();
      }),
    );
  }

  /**
   * Handles the store update flow without onboarding
   */
  handleStoreUpdateFlow(
    store: StoreGetDTO,
    param: StoreLinkQueryParams,
    claimTypes: ProviderClaimTypesGetDTO[],
    activatedRoute: ActivatedRoute,
    setViewState: (action: eventAction, description: string) => void,
    clearLinkQueryParams: () => void,
  ): Observable<StoreGetDTO[]> {
    const dialogRef = this.dialog.open(StoreUpdateDialogComponent, {
      data: {
        store,
        parentRoute: activatedRoute.snapshot,
        mode: "openAfterLinking",
        claimTypes: claimTypes,
      },
      width: "auto",
      maxWidth: "46vw",
      panelClass: "store-update-dialog-panel",
      autoFocus: false,
      disableClose: true,
    });

    return dialogRef.afterClosed().pipe(
      switchMap((res) => {
        if (this.shouldRefresh(res)) {
          return this.loadStores().pipe(
            tap((stores) => {
              setViewState(
                "view_refreshed",
                `User refreshed the Stores management view after adding a new store and closing the modal for store with id: ${param.id}`,
              );
            }),
          );
        }
        return EMPTY;
      }),
    );
  }

  /**
   * Handles the error flow when adding a store
   */
  handleFailureQueryParam(
    param: StoreLinkQueryParams,
    clearLinkQueryParams: () => void,
  ) {
    this.dialog.open(ConfirmComponent, {
      data: {
        message: `${this.translate.instant(
          "messages.There was an error while attempting to add the new store. See details below.",
        )} ${this.translate.instant("stores.unexpectedError")}${
          param.error_code ? `\nError: ${param.error_code}` : ""
        }`,
        clearButtonClose: true,
      },
    });
    clearLinkQueryParams();
  }

  /**
   * Opens the onboarding modal for a newly linked store
   */
  openOnboardingFlowForNewStore(
    store: StoreGetDTO,
    storeId: string,
    claimTypes: ProviderClaimTypesGetDTO[],
    activatedRoute: ActivatedRoute,
    setViewState: (action: eventAction, description: string) => void,
  ): Observable<StoreGetDTO[]> {
    const dialogRef = this.onboardingFlow.openMoment(
      "afterLinkingStore",
      activatedRoute.snapshot,
    );

    return dialogRef.afterClosed().pipe(
      switchMap(() => {
        const storeUpdateDialogRef = this.dialog.open(
          StoreUpdateDialogComponent,
          {
            data: {
              store,
              parentRoute: activatedRoute.snapshot,
              mode: "openAfterLinking",
              claimTypes: claimTypes,
            },
            width: "auto",
            maxWidth: "46vw",
            panelClass: "store-update-dialog-panel",
            autoFocus: false,
            disableClose: true,
          },
        );

        return storeUpdateDialogRef.afterClosed().pipe(
          switchMap((res) => {
            if (this.shouldRefresh(res)) {
              setViewState(
                "action_succeeded", `User called the API to get potential onboarding cases after completing the onboarding modal for store with id: ${storeId} and then refreshed the Stores management view`,
              );
              // Best-effort prefetch so the resolver doesn't re-fetch right
              // after this navigation — the backend already started the
              // potential-cases job itself when the store got linked.
              return this.onboardingModalService
                .prefetchOnboardingPotentialCases(storeId)
                .pipe(
                  catchError((error) => {
                    this.toast.error(
                      this.translate.instant(
                        "toast_errors.getOnboardingPotentialCasesFailed",
                      ),
                    );
                    return EMPTY;
                  }),
                  switchMap(() => this.loadStores()),
                  tap(() => {
                    setViewState(
                      "view_refreshed",
                      `User refreshed the Stores management view after adding a new store and closing the modal for store with id: ${storeId}`,
                    );

                    this.onboardingFlow.openMoment(
                      "afterCompleteDeliveryAndThresholdForm",
                      activatedRoute.snapshot,
                    );
                  }),
                );
            }
            return EMPTY;
          }),
        );
      }),
    );
  }

  /**
   * Deletes a store
   */
  deleteStore(store: StoreGetDTO): Observable<StoreGetDTO[]> {
    return this.dialog
      .open(ConfirmComponent, {
        data: {
          title: "Confirm delete",
          message: `Are you sure you want to delete ${store?.name}?`,
          okAction: "Delete",
        },
      })
      .afterClosed()
      .pipe(
        switchMap((confirmed) => {
          if (!confirmed) {
            return EMPTY;
          }

          this.loaderSvc.show();
          return this.storeService.delete(store.storeId, {
            message: "Store Deleted successfully!",
            severity: "success",
            duration: 3000,
          });
        }),
        switchMap(() => this.loadStores()),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 400) {
            const message =
              typeof error.error === "string"
                ? error.error
                : (error.error?.message ?? error.error?.error);
            if (message) {
              this.toast.error(message);
            }
          }
          return EMPTY;
        }),
        finalize(() => {
          this.loaderSvc.hide();
        }),
      );
  }

  /**
   * Updates a store
   */
  updateStore(
    store: StoreGetDTO,
    claimTypes: ProviderClaimTypesGetDTO[],
    activatedRoute: ActivatedRoute,
  ): Observable<StoreGetDTO[]> {
    const dialogRef = this.dialog.open(StoreUpdateDialogComponent, {
      data: {
        store,
        parentRoute: activatedRoute.snapshot,
        claimTypes: claimTypes,
      },
      autoFocus: false,
      disableClose: true,
    });

    return dialogRef.afterClosed().pipe(
      switchMap((res) => {
        if (this.shouldRefresh(res)) {
          this.loaderSvc.show();
          return this.loadStores();
        }
        return EMPTY;
      }),
      finalize(() => {
        this.loaderSvc.hide();
      }),
    );
  }

  /**
   * Updates delivery metadata
   */
  updateDeliveryMetadata(store: StoreGetDTO): Observable<StoreGetDTO[]> {
    this.loaderSvc.show();

    return this.storeService.loadStoreConfig(store.storeId).pipe(
      tap(() => this.loaderSvc.hide()),
      switchMap((config) => {
        const dialogRef = this.dialog.open(StoreUpdateDeliveryDialogComponent, {
          data: {
            store,
            storeConfig: config,
          },
          width: "auto",
          maxWidth: "40rem",
          panelClass: "store-update-dialog-panel",
          autoFocus: false,
          disableClose: true,
        });

        return dialogRef.afterClosed();
      }),
      switchMap((res) => {
        if (this.shouldRefresh(res)) {
          this.loaderSvc.show();
          return this.loadStores();
        }
        return EMPTY;
      }),
      catchError((error) => {
        this.toast.error(
          this.translate.instant("toast_errors.getStoreConfigFailed"),
        );
        return EMPTY;
      }),
      finalize(() => {
        this.loaderSvc.hide();
      }),
    );
  }

  /**
   * Updates threshold metadata
   */
  updateThresholdMetadata(
    store: StoreGetDTO,
    claimTypes: ProviderClaimTypesGetDTO[],
  ): Observable<StoreGetDTO[]> {
    this.loaderSvc.show();

    return this.storeService.loadStoreConfig(store.storeId).pipe(
      tap(() => this.loaderSvc.hide()),
      switchMap((config) => {
        const dialogRef = this.dialog.open(
          StoreUpdateThresholdDialogComponent,
          {
            data: {
              store,
              storeConfig: config,
              claimTypes: claimTypes,
            },
            width: "auto",
            maxWidth: "48rem",
            panelClass: "store-update-dialog-panel",
            autoFocus: false,
            disableClose: true,
          },
        );

        return dialogRef.afterClosed();
      }),
      switchMap((res) => {
        if (this.shouldRefresh(res)) {
          this.loaderSvc.show();
          return this.loadStores();
        }
        return EMPTY;
      }),
      catchError((error) => {
        this.toast.error(
          this.translate.instant("toast_errors.getStoreConfigFailed"),
        );
        return EMPTY;
      }),
      finalize(() => {
        this.loaderSvc.hide();
      }),
    );
  }

  /**
   * Completes store setup
   */
  completeSetup(
    store: StoreGetDTO,
    claimTypes: ProviderClaimTypesGetDTO[],
    activatedRoute: ActivatedRoute,
  ): Observable<StoreGetDTO[]> {
    this.loaderSvc.show();

    return this.storeService.loadStoreConfig(store.storeId).pipe(
      switchMap((config) => {
        this.loaderSvc.hide();
        const dialogRef = this.dialog.open(StoreUpdateDialogComponent, {
          data: {
            store,
            parentRoute: activatedRoute.snapshot,
            mode: "onlyCompleteSetup",
            claimTypes: claimTypes,
            storeConfig: config,
          },
          width: "auto",
          maxWidth: "46vw",
          panelClass: "store-update-dialog-panel",
          autoFocus: false,
          disableClose: true,
        });

        return dialogRef.afterClosed().pipe(
          switchMap((res) => {
            if (this.shouldRefresh(res)) {
              this.loaderSvc.show();
              return this.loadStores();
            }
            return EMPTY;
          }),
        );
      }),
      catchError((error) => {
        this.toast.error(
          this.translate.instant("toast_errors.getStoreConfigFailed"),
        );
        return EMPTY;
      }),
      finalize(() => {
        this.loaderSvc.hide();
      }),
    );
  }

  /**
   * Same as `completeSetup`, but for a store still mid first-time onboarding:
   * once the delivery/threshold form is saved, this also ensures the
   * onboarding-potential-cases job is requested and opens the "continue to
   * claims" modal — the same steps the newly-linked-store flow
   * (`openOnboardingFlowForNewStore`) runs after its own delivery/threshold
   * form. Without this, a store reached via `/store/stores` directly (e.g.
   * the dashboard's "resume incomplete store setup" prompt) would finish
   * configuration but never continue the onboarding flow.
   */
  completeSetupForOnboarding(
    store: StoreGetDTO,
    claimTypes: ProviderClaimTypesGetDTO[],
    activatedRoute: ActivatedRoute,
  ): Observable<StoreGetDTO[]> {
    const storeId = store.storeId;
    return this.completeSetup(store, claimTypes, activatedRoute).pipe(
      switchMap((stores) =>
        this.onboardingModalService.prefetchOnboardingPotentialCases(storeId).pipe(
          catchError(() => {
            this.toast.error(
              this.translate.instant(
                "toast_errors.getOnboardingPotentialCasesFailed",
              ),
            );
            return EMPTY;
          }),
          tap(() => {
            this.onboardingFlow.openMoment(
              "afterCompleteDeliveryAndThresholdForm",
              activatedRoute.snapshot,
            );
          }),
          switchMap(() => of(stores)),
        ),
      ),
    );
  }

  /**
   * Loads stores from the StoreService cache.
   * Operations that modify stores already update the internal signal.
   */
  loadStores(): Observable<StoreGetDTO[]> {
    return this.storeService.getCachedOrLoad();
  }

  /**
   * Checks whether a refresh is needed
   */
  private shouldRefresh(res: any): boolean {
    return res?.event === "Refresh";
  }
}
